import { useId, useMemo, useSyncExternalStore } from 'react'
import { pixelAvatarsOn, subscribePixelAvatars } from './avatarPref'

type PixelPetShape = 'orb' | 'shield' | 'pebble' | 'stamp' | 'capsule' | 'gem'

type PixelPetDescriptor = {
  shape: PixelPetShape
  hue: number
  hue2: number
  cheekHue: number
  eye: 0 | 1 | 2 | 3
  mouth: 0 | 1 | 2 | 3
  mark: 0 | 1 | 2 | 3
  accessory: 0 | 1 | 2 | 3 | 4 | 5
  pattern: 0 | 1 | 2 | 3
  faceX: number
  faceY: number
  tilt: number
}

const SHAPES: Record<PixelPetShape, string> = {
  orb:
    'M32 4C46.2 4 58 15.2 58 31.2 58 48 47.2 60 31.5 60 15.4 60 6 48.7 6 32 6 16.6 17 4 32 4Z',
  shield:
    'M32 4C45.4 4 56.5 12 58 25.4 60 43 48.3 58.5 32 60 15.8 58.6 4 43 6 25.4 7.5 12 18.6 4 32 4Z',
  pebble:
    'M34 4C48.8 5.2 58 17.1 57.3 33.5 56.7 49.2 45 59.5 29.8 59.7 14.8 59.8 5.2 48.2 6 31.3 6.7 15.6 18.2 2.7 34 4Z',
  stamp:
    'M21.5 5.8C28.7 2.7 40.3 3.3 47 7.3 54.3 11.7 59 20.4 58 31.3 57.2 41.8 53.2 51 45.5 56 38 60.9 26.2 61 18.6 56.3 10.5 51.2 6 42.2 6 31.6 6 20 11.2 10.2 21.5 5.8Z',
  capsule:
    'M20 6h24c8.8 0 16 7.2 16 16v20c0 8.8-7.2 16-16 16H20C11.2 58 4 50.8 4 42V22C4 13.2 11.2 6 20 6Z',
  gem:
    'M32 4 52 12 60 32 52 52 32 60 12 52 4 32 12 12 32 4Z',
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function rand(seed: number) {
  let x = seed || 1
  return () => {
    x |= 0
    x = (x + 0x6d2b79f5) | 0
    let t = Math.imul(x ^ (x >>> 15), 1 | x)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(items: readonly T[], value: number): T {
  return items[Math.floor(value * items.length)] ?? items[0]
}

function describe(seed: string): PixelPetDescriptor {
  const r = rand(hashString(seed))
  const shapes: PixelPetShape[] = ['orb', 'shield', 'pebble', 'stamp', 'capsule', 'gem']
  const hue = Math.floor(r() * 360)
  return {
    shape: pick(shapes, r()),
    hue,
    hue2: (hue + 75 + Math.floor(r() * 140)) % 360,
    cheekHue: (hue + 185 + Math.floor(r() * 80)) % 360,
    eye: Math.floor(r() * 4) as 0 | 1 | 2 | 3,
    mouth: Math.floor(r() * 4) as 0 | 1 | 2 | 3,
    mark: Math.floor(r() * 4) as 0 | 1 | 2 | 3,
    accessory: Math.floor(r() * 6) as 0 | 1 | 2 | 3 | 4 | 5,
    pattern: Math.floor(r() * 4) as 0 | 1 | 2 | 3,
    faceX: Math.round((r() - 0.5) * 4),
    faceY: Math.round((r() - 0.5) * 5),
    tilt: Math.round((r() - 0.5) * 8),
  }
}

function hsl(h: number, s: number, l: number, alpha = 1): string {
  return `hsl(${h} ${s}% ${l}% / ${alpha})`
}

function Eyes({ kind, blink = false }: { kind: PixelPetDescriptor['eye']; blink?: boolean }) {
  if (blink || kind === 1) {
    return (
      <>
        <path d="M21 29h7" stroke="#11111b" strokeWidth="3" strokeLinecap="round" />
        <path d="M37 29h7" stroke="#11111b" strokeWidth="3" strokeLinecap="round" />
      </>
    )
  }
  if (kind === 2) {
    return (
      <>
        <circle cx="24" cy="29" r="3.8" fill="#11111b" />
        <circle cx="40" cy="29" r="2.5" fill="#11111b" />
      </>
    )
  }
  if (kind === 3) {
    return (
      <>
        <rect x="20" y="25" width="8" height="7" rx="2" fill="#11111b" />
        <rect x="36" y="25" width="8" height="7" rx="2" fill="#11111b" />
      </>
    )
  }
  return (
    <>
      <circle cx="24" cy="29" r="3.2" fill="#11111b" />
      <circle cx="40" cy="29" r="3.2" fill="#11111b" />
    </>
  )
}

function Mouth({ kind }: { kind: PixelPetDescriptor['mouth'] }) {
  if (kind === 1) {
    return <path d="M27 38c2.5 3 7.5 3 10 0" stroke="#11111b" strokeWidth="2.7" strokeLinecap="round" fill="none" />
  }
  if (kind === 2) {
    return (
      <>
        <path d="M28 39l8 2" stroke="#11111b" strokeWidth="2.7" strokeLinecap="round" />
        <circle cx="37.5" cy="41.3" r="1.45" fill="#11111b" />
      </>
    )
  }
  if (kind === 3) {
    return (
      <>
        <path d="M28 41l8-2" stroke="#11111b" strokeWidth="2.7" strokeLinecap="round" />
        <circle cx="37.5" cy="38.7" r="1.45" fill="#11111b" />
      </>
    )
  }
  return (
    <>
      <path d="M28 38.5c1.8 2.2 6.2 2.2 8 0" stroke="#11111b" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="37.5" cy="38.5" r="1.35" fill="#11111b" />
    </>
  )
}

function Mark({ kind, hue }: { kind: PixelPetDescriptor['mark']; hue: number }) {
  const fill = hsl(hue, 86, 75, 0.92)
  if (kind === 1) return <path d="M32 11 39 22H25L32 11Z" fill={fill} />
  if (kind === 2) {
    return (
      <>
        <circle cx="24" cy="17" r="3" fill={fill} />
        <circle cx="32" cy="14.5" r="2.7" fill={fill} />
        <circle cx="40" cy="17" r="3" fill={fill} />
      </>
    )
  }
  if (kind === 3) {
    return (
      <>
        <path d="M24 15h16" stroke={fill} strokeWidth="3.5" strokeLinecap="round" />
        <path d="M28 20h8" stroke={fill} strokeWidth="3" strokeLinecap="round" />
      </>
    )
  }
  return <path d="M25 17c4-3.8 10-3.8 14 0" stroke={fill} strokeWidth="4" strokeLinecap="round" fill="none" />
}

function Accessory({ kind, hue }: { kind: PixelPetDescriptor['accessory']; hue: number }) {
  const fill = hsl(hue, 78, 68)
  if (kind === 1) {
    return (
      <>
        <path d="M17 13 11 5 10 18Z" fill={fill} stroke="#181825" strokeWidth="3" strokeLinejoin="round" />
        <path d="M47 13 53 5 54 18Z" fill={fill} stroke="#181825" strokeWidth="3" strokeLinejoin="round" />
      </>
    )
  }
  if (kind === 2) {
    return (
      <>
        <path d="M22 10 18 2" stroke={fill} strokeWidth="4" strokeLinecap="round" />
        <path d="M42 10 46 2" stroke={fill} strokeWidth="4" strokeLinecap="round" />
        <circle cx="17.5" cy="2.8" r="3" fill={fill} />
        <circle cx="46.5" cy="2.8" r="3" fill={fill} />
      </>
    )
  }
  if (kind === 3) {
    return <path d="M17 15c8-8 22-8 30 0" stroke={fill} strokeWidth="6" strokeLinecap="round" fill="none" />
  }
  if (kind === 4) {
    return <path d="M19 9h26l4 8H15l4-8Z" fill={fill} stroke="#181825" strokeWidth="3" strokeLinejoin="round" />
  }
  if (kind === 5) {
    return (
      <>
        <circle cx="19" cy="17" r="4" fill={fill} />
        <circle cx="45" cy="17" r="4" fill={fill} />
      </>
    )
  }
  return null
}

function Pattern({ kind, hue }: { kind: PixelPetDescriptor['pattern']; hue: number }) {
  const stroke = hsl(hue, 92, 80, 0.42)
  if (kind === 1) {
    return (
      <>
        <path d="M5 25h54" stroke={stroke} strokeWidth="5" />
        <path d="M5 39h54" stroke={stroke} strokeWidth="4" />
      </>
    )
  }
  if (kind === 2) {
    return (
      <>
        <circle cx="18" cy="47" r="5" fill={stroke} />
        <circle cx="47" cy="20" r="4" fill={stroke} />
        <circle cx="48" cy="45" r="3" fill={stroke} />
      </>
    )
  }
  if (kind === 3) return <path d="M9 55 55 9" stroke={stroke} strokeWidth="8" />
  return <path d="M-2 49C11 42 22 43 34 49s22 8 34-1v18H-2V49Z" fill={hsl(hue, 82, 34, 0.38)} />
}

/**
 * A hash-seeded PixelPad headshot for an agent.
 *
 * It is built as a compact headshot rather than a full-body pixel sprite: each seed
 * chooses a silhouette, palette, facial layout, marking, accessory, and surface pattern.
 */
export function PixelPet({
  name,
  seed,
  size = 24,
  frame = 1,
  active = false,
}: {
  name: string
  seed?: string | null | undefined
  size?: number
  frame?: 1 | 2
  active?: boolean
}) {
  const rawId = useId()
  const id = rawId.replace(/[^a-zA-Z0-9_-]/g, '')
  const d = useMemo(() => describe(seed || name), [name, seed])
  const shape = SHAPES[d.shape]
  const blink = active || frame === 2
  /*
   * The whole-app switch (owner, 2026-08-26): with pixel avatars off, every
   * pet is a simple small dot in the pet's own hue — the identity colour
   * stays (a roster of identical grey circles would make every row anonymous),
   * the face goes. Subscribed here, at the leaf, so ONE setter in the menu
   * flips all thirty-odd render sites live with no prop threading.
   */
  const petsOn = useSyncExternalStore(subscribePixelAvatars, pixelAvatarsOn, pixelAvatarsOn)
  if (!petsOn) {
    return (
      <span className="pet-wrap pet-dot" style={{ width: size, height: size }} title={name}>
        <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
          <circle cx="32" cy="32" r="13" fill={hsl(d.hue, 60, 62)} />
        </svg>
      </span>
    )
  }

  return (
    <span
      className={`pet-wrap pixelpad${active ? ' is-active' : ''}`}
      style={{ width: size, height: size }}
      title={name}
    >
      <svg
        className="pixelpad-svg"
        viewBox="0 0 64 64"
        width={size}
        height={size}
        role="img"
        aria-label={`${name} agent headshot`}
      >
        <defs>
          <linearGradient id={`${id}-body`} x1="12" y1="7" x2="52" y2="57" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={hsl(d.hue, 80, 72)} />
            <stop offset="1" stopColor={hsl(d.hue2, 82, 50)} />
          </linearGradient>
          <clipPath id={`${id}-clip`}>
            <path d={shape} />
          </clipPath>
        </defs>
        <g className="pixelpad-bob" style={{ transformOrigin: '32px 34px' }}>
          <g className="pixelpad-wiggle" style={{ transformOrigin: '32px 34px' }}>
            <g transform="translate(32 32) scale(1.25) translate(-32 -32)">
              <Accessory kind={d.accessory} hue={d.cheekHue} />
              <path d={shape} fill={`url(#${id}-body)`} />
              <g clipPath={`url(#${id}-clip)`}>
                <circle cx="16" cy="13" r="19" fill="rgba(255,255,255,.18)" />
                <circle cx="50" cy="52" r="22" fill="rgba(17,17,27,.22)" />
                <Pattern kind={d.pattern} hue={d.hue2} />
                <path d="M10 10 55 55" stroke="rgba(255,255,255,.14)" strokeWidth="5" />
              </g>
              <path d={shape} fill="none" stroke="#181825" strokeWidth="4.5" />
              <path d={shape} fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="1.6" />
              <g transform={`translate(${d.faceX} ${d.faceY}) rotate(${d.tilt} 32 34)`}>
                <Mark kind={d.mark} hue={d.cheekHue} />
                <Eyes kind={d.eye} blink={blink} />
                <circle cx="19" cy="36" r="4" fill={hsl(d.cheekHue, 88, 76, 0.45)} />
                <circle cx="45" cy="36" r="4" fill={hsl(d.cheekHue, 88, 76, 0.45)} />
                <Mouth kind={d.mouth} />
              </g>
              {active && (
                <g className="pixelpad-spark" aria-hidden="true">
                  <circle cx="48" cy="16" r="3" fill="#a6e3a1" />
                  <path d="M52 8v7M48.5 11.5h7" stroke="#89dceb" strokeWidth="2" strokeLinecap="round" />
                </g>
              )}
            </g>
          </g>
        </g>
      </svg>
    </span>
  )
}
